import { useState, type ReactElement } from 'react';
import { CHANNEL_RULES } from '@gr/core/distribution/channels/channel-types';
import type { ChannelDto, WeeklySlot } from '@gr/core/distribution/channels/dto';
import { useT } from '../../../../app/di';
import { Alert } from '../../../../ui-kit/Alert/Alert';
import { Badge } from '../../../../ui-kit/Badge/Badge';
import { Button } from '../../../../ui-kit/Button/Button';
import { Card } from '../../../../ui-kit/Card/Card';
import { useConfirm } from '../../../../ui-kit/ConfirmDialog/ConfirmDialog';
import type { ServerOutcome } from '../../../../ui-kit/form/server-errors';
import { ChannelForm, type ChannelValues } from '../ChannelForm/ChannelForm';
import { SlotGridEditor } from '../SlotGridEditor/SlotGridEditor';

export interface ChannelCardProps {
  channel: ChannelDto;
  /** Часовой пояс блога — в нём заданы слоты. */
  timezone: string;
  pending: boolean;
  savingSlots: boolean;
  /** Коды ошибки последнего действия. */
  error: readonly string[] | null;
  onToggleActive(): void;
  onDelete(): void;
  onSaveDetails(values: ChannelValues): Promise<ServerOutcome>;
  onSaveSlots(slots: WeeklySlot[]): Promise<ServerOutcome>;
}

/** CHN-UC-2…5: канал с правилами, правкой, сеткой слотов, включением и удалением. */
export function ChannelCard({ channel, timezone, pending, savingSlots, error, onToggleActive, onDelete, onSaveDetails, onSaveSlots }: ChannelCardProps): ReactElement {
  const { t, te } = useT();
  const confirm = useConfirm();
  const [slotsSaved, setSlotsSaved] = useState(false);
  const rules = CHANNEL_RULES[channel.type];
  const limit = (value: number | null): string => (value === null ? t('channels.noLimit') : String(value));

  const toggle = async (): Promise<void> => {
    // CHN-UC-2.CC-1: отключение снимает запланированные публикации — сначала спросить.
    if (channel.active && channel.plannedCount > 0) {
      if (!(await confirm({ title: t('channels.disableConfirm', { n: channel.plannedCount }), confirmLabel: t('channels.disable') }))) return;
    }
    onToggleActive();
  };

  const remove = async (): Promise<void> => {
    if (await confirm({ title: t('channels.deleteConfirm', { name: channel.name }), confirmLabel: t('common.delete'), danger: true })) onDelete();
  };

  return (
    <Card
      title={
        <span className="u-row">
          <span className="u-truncate">{channel.name}</span>
          <Badge tone={channel.active ? 'success' : 'neutral'}>{channel.active ? t('channels.active') : t('channels.inactive')}</Badge>
          <Badge>{t(`channels.types.${channel.type}`)}</Badge>
        </span>
      }
      actions={
        <>
          <Button size="sm" loading={pending} onClick={() => void toggle()}>
            {channel.active ? t('channels.disable') : t('channels.enable')}
          </Button>
          <Button size="sm" variant="ghost" icon="trash" disabled={pending} onClick={() => void remove()}>
            {t('common.delete')}
          </Button>
        </>
      }
    >
      <div className="u-stack">
        {error ? <Alert tone="danger">{te(error)}</Alert> : null}
        <p className="u-muted u-small">
          {t('channels.rules', { caption: limit(rules.captionLimit), hashtags: limit(rules.hashtagLimit), media: limit(rules.mediaLimit) })}
          {' · '}
          {t('channels.stats', { planned: channel.plannedCount, history: channel.historyCount })}
        </p>
        <ChannelForm initial={{ type: channel.type, name: channel.name, url: channel.url }} withType={false} submitLabel={t('common.save')} onSubmit={onSaveDetails} />
        <h3>{t('channels.slots.title')}</h3>
        <p className="u-muted u-small">{t('channels.slots.hint', { tz: timezone })}</p>
        {slotsSaved ? <Alert tone="success">{t('channels.slots.saved')}</Alert> : null}
        <SlotGridEditor
          slots={channel.slots}
          disabled={false}
          saving={savingSlots}
          onSave={async (slots) => {
            setSlotsSaved(false);
            setSlotsSaved((await onSaveSlots(slots)).success);
          }}
        />
      </div>
    </Card>
  );
}
